'use client';

import React, { useState, useRef, useEffect } from 'react';
import { Upload, ImageIcon, X } from 'lucide-react';
import AppImage from '@/components/ui/AppImage';
import { createClient } from '@/lib/supabase/client';

interface GuestPhoto {
  id: string;
  public_url: string;
  uploader_name: string;
  caption?: string;
}

export default function GuestGallery() {
  const supabase = createClient();
  const [photos, setPhotos] = useState<GuestPhoto[]>([]);
  const [uploaderName, setUploaderName] = useState('');
  const [caption, setCaption] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    fetchPhotos();
  }, []);

  const fetchPhotos = async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('guest_photos')
        .select('id, public_url, uploader_name, caption')
        .order('uploaded_at', { ascending: false });

      if (error) {
        console.error('Error fetching guest photos:', error.message);
      } else {
        setPhotos(data || []);
      }
    } catch (err) {
      console.error('Unexpected error fetching photos:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleFile = (file: File) => {
    if (!file.type.startsWith('image/')) {
      setError('Please upload an image file (JPG, PNG, HEIC, etc.)');
      return;
    }
    setSelectedFile(file);
    const reader = new FileReader();
    reader.onload = (e) => setPreview(e.target?.result as string);
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFile(file);
  };

  const handleUpload = async () => {
    if (!selectedFile) { setError('Please select a photo to upload.'); return; }
    if (!uploaderName.trim()) { setError('Please enter your name.'); return; }

    setUploading(true);
    setError(null);

    const fileExt = selectedFile.name.split('.').pop();
    const fileName = `${Date.now()}-${Math.random().toString(36).substring(2)}.${fileExt}`;

    const { error: uploadError } = await supabase.storage
      .from('guest-photos')
      .upload(fileName, selectedFile, { cacheControl: '3600', upsert: false });

    if (uploadError) {
      setError('Upload failed. Please try again.');
      setUploading(false);
      return;
    }

    const { data: urlData } = supabase.storage
      .from('guest-photos')
      .getPublicUrl(fileName);

    const publicUrl = urlData.publicUrl;

    const { data: inserted, error: dbError } = await supabase
      .from('guest_photos')
      .insert({
        storage_path: fileName,
        public_url: publicUrl,
        uploader_name: uploaderName.trim(),
        caption: caption.trim() || null,
      })
      .select('id, public_url, uploader_name, caption')
      .single();

    if (dbError) {
      setError('Could not save photo details. Please try again.');
      setUploading(false);
      return;
    }

    setPhotos((prev) => [inserted, ...prev]);
    setPreview(null);
    setSelectedFile(null);
    setUploaderName('');
    setCaption('');
    setUploading(false);
    setSuccessMsg('Your photo has been added to the gallery. Thank you!');
    setTimeout(() => setSuccessMsg(null), 5000);
  };

  return (
    <section className="bg-off-white py-24 px-8 md:px-16 xl:px-24 border-t border-border">
      <div className="max-w-screen-2xl mx-auto">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-14">
          <div>
            <p className="section-label mb-4">Your Photographs</p>
            <h2 className="font-serif text-display-md text-faded-navy">
              Share Your Memories
            </h2>
            <p className="font-sans text-sm text-stone mt-4 max-w-lg leading-relaxed">
              We would love to see the day through your eyes. Upload your favourite photographs from the weekend and they will appear here for everyone to enjoy.
            </p>
          </div>
          <p className="font-sans text-xs text-stone tracking-[0.1em] shrink-0">
            {photos.length} photograph{photos.length !== 1 ? 's' : ''} shared
          </p>
        </div>

        {/* Upload area */}
        <div className="mb-16 border border-border bg-buttermilk p-8 md:p-10">
          <p className="text-xs tracking-[0.2em] uppercase font-sans text-stone mb-8">
            Upload a Photo
          </p>

          {successMsg && (
            <div className="mb-6 px-5 py-4 bg-olive/10 border border-olive/30 text-olive font-sans text-sm">
              {successMsg}
            </div>
          )}

          {error && (
            <div className="mb-6 px-5 py-4 bg-red-50 border border-red-200 text-red-600 font-sans text-sm">
              {error}
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-10">
            {/* Drop zone */}
            <div>
              <div
                className={`relative border-2 border-dashed transition-all duration-300 cursor-pointer flex flex-col items-center justify-center py-16 px-8 text-center ${
                  dragOver
                    ? 'border-warm-sand bg-warm-sand/5'
                    : preview
                    ? 'border-olive/30' :'border-border hover:border-warm-sand/50'
                }`}
                onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
                onDragLeave={() => setDragOver(false)}
                onDrop={handleDrop}
                onClick={() => !preview && fileInputRef.current?.click()}
              >
                {preview ? (
                  <div className="relative w-full aspect-square max-w-xs">
                    <img
                      src={preview}
                      alt="Preview of photo to be uploaded"
                      className="w-full h-full object-cover"
                    />
                    <button
                      type="button"
                      onClick={(e) => { e.stopPropagation(); setPreview(null); setSelectedFile(null); }}
                      className="absolute top-2 right-2 bg-deep-navy/70 text-white rounded-full p-1 hover:bg-deep-navy transition-colors duration-200"
                      aria-label="Remove selected photo"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload size={28} strokeWidth={1} className="text-warm-sand mb-5" />
                    <p className="font-sans text-sm text-stone mb-2">Drop a photo here</p>
                    <p className="font-sans text-xs text-stone/50">or click to browse</p>
                    <p className="font-sans text-xs text-stone/40 mt-4">JPG, PNG, HEIC up to 20MB</p>
                  </>
                )}
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={(e) => { const f = e.target.files?.[0]; if (f) handleFile(f); }}
                aria-label="Upload photo file"
              />
            </div>

            {/* Form fields */}
            <div className="space-y-7 flex flex-col justify-center">
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                  Your Name <span className="text-red-400">*</span>
                </label>
                <input
                  type="text"
                  placeholder="How you would like to be credited"
                  value={uploaderName}
                  onChange={(e) => setUploaderName(e.target.value)}
                  className="input-field"
                />
              </div>
              <div>
                <label className="block text-xs tracking-[0.15em] uppercase font-sans text-stone mb-3">
                  Caption
                </label>
                <p className="text-xs font-sans text-stone/50 mb-3">Optional, a word or two about the moment</p>
                <input
                  type="text"
                  placeholder="A short caption..."
                  value={caption}
                  onChange={(e) => setCaption(e.target.value)}
                  className="input-field"
                />
              </div>
              <button
                type="button"
                onClick={handleUpload}
                disabled={uploading || !preview}
                className="flex items-center justify-center gap-3 w-full py-4 text-xs tracking-[0.3em] uppercase font-sans bg-faded-navy text-buttermilk hover:bg-deep-navy transition-all duration-400 disabled:opacity-50 disabled:cursor-not-allowed"
                style={{ minHeight: '52px' }}
              >
                {uploading ? (
                  <>
                    <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                    </svg>
                    Uploading
                  </>
                ) : (
                  <>
                    <ImageIcon size={14} strokeWidth={1.5} />
                    Share Photo
                  </>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Guest photo grid */}
        {loading ? (
          <div className="text-center py-20">
            <svg className="animate-spin w-8 h-8 text-warm-sand mx-auto" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          </div>
        ) : photos.length > 0 ? (
          <div className="columns-2 md:columns-3 xl:columns-4 2xl:columns-4 gap-1 space-y-1">
            {photos.map((photo) => (
              <div key={photo.id} className="break-inside-avoid relative group overflow-hidden">
                <AppImage
                  src={photo.public_url}
                  alt={`Photo shared by ${photo.uploader_name}`}
                  width={600}
                  height={400}
                  className="w-full h-auto object-cover transition-transform duration-600 group-hover:scale-105"
                  sizes="(max-width: 768px) 50vw, 25vw"
                />
                <div className="absolute inset-0 bg-deep-navy/50 opacity-0 group-hover:opacity-100 transition-opacity duration-400 flex flex-col justify-end p-4">
                  {photo.caption && (
                    <p className="font-sans text-xs text-off-white mb-1">{photo.caption}</p>
                  )}
                  <p className="font-sans text-xs text-off-white/60">{photo.uploader_name}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20 border border-dashed border-border">
            <ImageIcon size={32} strokeWidth={1} className="text-warm-sand mx-auto mb-6" />
            <p className="font-serif text-xl text-faded-navy mb-3">No photos yet</p>
            <p className="font-sans text-sm text-stone">
              Be the first to share a memory from the weekend.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}
